import apiClient from '@/services/api'

export default {
  async getProjectStatistics({ rootState }, { start, end, role }) {
    let paymentType = null
    if (rootState.applicationState.currentPaymentType) {
      paymentType = rootState.applicationState.currentPaymentTypeName.toLowerCase()
    }

    const { data } = await apiClient.post('/api/statistic/project/get', {
      project_id: rootState.applicationState.currentProject.id,
      payment_type: paymentType,
      start: start ?? null,
      end: end ?? null,
      role: role ? { id: role } : null
    })

    return data
  },

  async getProjectByTaxId({ rootState }, { tax_id, exclude_id }) {
    const exclude_ids = [exclude_id ?? rootState.applicationState.currentProject.id]

    try {
      const { data } = await apiClient.get(`/api/project/tax_id/${tax_id}?fields={name,id}`, {
        params: {
          exclude_ids
        }
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }

      return data.project
    } catch (error) {
      throw error.message
    }
  },

  async getClients({ rootState }, { page, size, fields = '{*}' }) {
    const project_id = rootState.applicationState.currentProject.id

    try {
      const { data } = await apiClient.get(`/api/project/${project_id}/staffing/facility?fields=${fields}`, {
        params: { page, size }
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }

      return data
    } catch (error) {
      throw error.message
    }
  },

  async getClientsAssociates({ rootState }, { page, size, associate_types, staff_project_id, fields = '{*}' }) {
    const project_id = rootState.applicationState.currentProject.id

    try {
      const { data } = await apiClient.get(
        `/api/project/${project_id}/staffing/facility/${staff_project_id}/staff_associates?fields=${fields}`,
        {
          params: { page, size, associate_types }
        }
      )

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }

      return data
    } catch (error) {
      throw error.message
    }
  },

  async getClientsManagement({ rootState }, { facility_project_id }) {
    const project_id = rootState.applicationState.currentProject.id

    try {
      const { data } = await apiClient.get(`/api/project/${project_id}/facility/${facility_project_id}/managements`)

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }

      return data.associates
    } catch (error) {
      throw error.message
    }
  },

  async getSchedulerLink({ rootState }, { target_url }) {
    try {
      const { data } = await apiClient.post('/api/scheduler_external/auth/generate_link', {
        target_url,
        email: rootState.applicationState.associate.email
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }

      return data.url
    } catch (error) {
      throw error.message
    }
  },

  async postSwitchProject({ dispatch }, { project_id }) {
    try {
      const { data } = await apiClient.post('/api/switch_current_project/new', {
        project_id
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }
      await dispatch('loadApplicationState', null, { root: true })
    } catch (error) {
      throw error.message
    }
  },

  async postCreateProject({ rootState, dispatch }, { project }) {
    try {
      const { data } = await apiClient.post('/api/send_creation_project_form', {
        ...project,
        user_id: rootState.applicationState.user.id
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg ?? 'Error while creating project'}`)
      }

      await dispatch('postSwitchProject', {
        project_id: data.project_id
      })

      await dispatch('loadApplicationState', null, { root: true })
    } catch (error) {
      throw error.message
    }
  },

  async patchUpdateProject({ dispatch }, { project, logo_file_id }) {
    try {
      const { data } = await apiClient.post('/api/project/update', {
        project,
        logo_file_id
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg ?? 'Error while updating project'}`)
      }

      await dispatch('loadApplicationState', null, { root: true })
    } catch (error) {
      throw error.message
    }
  },

  async getBusinessHours(_, { project_id }) {
    try {
      const { data } = await apiClient.get(`/api/project/${project_id}/buisines_hours`)

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg ?? 'Error while loading business hours'}`)
      }

      return data
    } catch (error) {
      throw error.message
    }
  },

  async saveBusinessHours(_, { project_id, hours }) {
    try {
      const { data } = await apiClient.post(`/api/project/${project_id}/buisines_hours`, { hours })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg ?? 'Error while saving business hours'}`)
      }

      return data
    } catch (error) {
      throw error.message
    }
  },

  async getProjectFeatures({ rootState, commit }) {
    const project_id = rootState.applicationState.currentProject.id

    try {
      const { data } = await apiClient.post(`/api/project/${project_id}/features/get`)

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg ?? 'Error while updating features'}`)
      }

      const updatedCurrentProject = {
        ...rootState.applicationState.currentProject,
        features: data.features
      }

      commit('setCurrentProject', updatedCurrentProject, { root: true })

      return data.features
    } catch (error) {
      throw error.message
    }
  },

  async postProjectFeatures({ rootState, commit, dispatch }, { features }) {
    const expectedSessionId = rootState.applicationState.sessionId
    const project_id = rootState.applicationState.currentProject.id

    try {
      const { data } = await apiClient.post(`/api/project/${project_id}/features/update`, { features })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg ?? 'Error while updating features'}`)
      }

      const updatedCurrentProject = {
        ...rootState.applicationState.currentProject,
        features: data.features
      }

      commit('setCurrentProject', updatedCurrentProject, { root: true })
      dispatch('updateLocalStorage', { expectedSessionId }, { root: true })

      return data.features
    } catch (error) {
      throw error.message
    }
  },

  async getProjectTimeConstants({ rootState, commit }) {
    const project_id = rootState.applicationState.currentProject.id

    try {
      const { data } = await apiClient.post('/api/get_project_time_constants', {
        project_id
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg ?? 'Error while updating time constants'}`)
      }

      const updatedTimeConstants = {
        ...data.time_constants
      }

      commit('setTimeConstants', updatedTimeConstants, { root: true })
      return data.time_constants
    } catch (error) {
      throw error.message
    }
  },

  async postProjectTimeConstants({ rootState, commit, dispatch }, { time_constants }) {
    const expectedSessionId = rootState.applicationState.sessionId
    const project_id = rootState.applicationState.currentProject.id

    try {
      const { data } = await apiClient.post('/api/set_project_time_constants', {
        time_constants,
        project_id
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg ?? 'Error while updating time constants'}`)
      }

      const updatedTimeConstants = {
        ...data.time_constants
      }

      commit('setTimeConstants', updatedTimeConstants, { root: true })
      dispatch('updateLocalStorage', { expectedSessionId }, { root: true })

      return data.time_constants
    } catch (error) {
      throw error.message
    }
  },

  async acceptJoinRequest(_, { join_project_request_id, associate }) {
    try {
      await apiClient.post('/api/accept_join_project_request', {
        join_project_request_id,
        associate
      })
    } catch (error) {
      throw error.message
    }
  },

  async rejectJoinRequest(_, { join_project_request_id, reason }) {
    try {
      await apiClient.post('/api/reject_join_project_request', {
        join_project_request_id,
        reason
      })
    } catch (error) {
      throw error.message
    }
  },

  async getProjectDisciplinaryActions({ rootState, commit }, _) {
    const project_id = rootState.applicationState.currentProject.id

    try {
      const { data } = await apiClient.get(`/api/project/${project_id}/disciplinary_settings`)

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }

      commit('SET_DISCIPLINARY_SETTINGS', data.items)
    } catch (error) {
      throw error.message
    }
  },

  async postProjectDisciplinaryActions({ rootState, commit }, { disciplinary_settings }) {
    const project_id = rootState.applicationState.currentProject.id

    try {
      const { data } = await apiClient.post(`/api/project/${project_id}/disciplinary_settings`, {
        ...disciplinary_settings
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }

      commit('SET_DISCIPLINARY_SETTINGS', data.items)
    } catch (error) {
      throw error.message
    }
  },
  async getProjectPaymentSettings({ rootState, commit }, _) {
    const project_id = rootState.applicationState.currentProject.id

    try {
      const { data } = await apiClient.post('/api/project/payment_settings/get', { project_id })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }

      commit('SET_PAYMENT_SETTINGS', data.payment_settings)
    } catch (error) {
      throw error.message
    }
  },

  async postProjectPaymentSettings({ rootState, commit }, { payment_settings }) {
    const project_id = rootState.applicationState.currentProject.id

    try {
      const { data } = await apiClient.post('/api/project/payment_settings/update', { ...payment_settings, project_id })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }

      commit('SET_PAYMENT_SETTINGS', data.payment_settings)
      commit(
        'setCurrentProject',
        {
          ...rootState.applicationState.currentProject,
          payment_settings: data.payment_settings
        },
        { root: true }
      )

      return data
    } catch (error) {
      throw error.message
    }
  },

  async postProjectMinimumPayChange({ rootState }, { minimum_pay }) {
    const project_id = rootState.applicationState.currentProject.id

    try {
      const { data } = await apiClient.post('/api/project/minimum_pay/change', {
        ...minimum_pay,
        project_id
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }

      return data
    } catch (error) {
      throw error.message
    }
  },

  async getProjectDayPositions({ rootState }) {
    const project_id = rootState.applicationState.currentProject.id

    try {
      const { data } = await apiClient.get('/api/project_day_positions', {
        params: { project_id }
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }

      return data.day_positions ?? []
    } catch (error) {
      throw error.message
    }
  },

  async patchProjectDayPosition(_, { project_day_position_id, work_day_start }) {
    try {
      const { data } = await apiClient.patch(`/api/project_day_positions/${project_day_position_id}`, {
        work_day_start
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }

      return data
    } catch (error) {
      throw error.message
    }
  },

  async postProjectDayPositionItem(_, { project_day_position, name, start, end }) {
    try {
      const { data } = await apiClient.post('/api/project_day_position_items', {
        project_day_position,
        name,
        start,
        end
      })

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }

      return data
    } catch (error) {
      throw error.message
    }
  },

  async patchProjectDayPositionItem(_, { project_day_position_item_id, name, start, end }) {
    try {
      const payload = {}

      if (name !== undefined) payload.name = name
      if (start !== undefined) payload.start = start
      if (end !== undefined) payload.end = end

      const { data } = await apiClient.patch(`/api/project_day_position_items/${project_day_position_item_id}`, payload)

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }

      return data
    } catch (error) {
      throw error.message
    }
  },

  async deleteProjectDayPositionItem(_, { project_day_position_item_id }) {
    try {
      const { data } = await apiClient.delete(`/api/project_day_position_items/${project_day_position_item_id}`)

      if (data.error_code && data.error_code !== '0') {
        throw new Error(`${data.msg}`)
      }

      return data
    } catch (error) {
      throw error.message
    }
  }
}
